import { withDatasourceCheck } from "@sitecore-jss/sitecore-jss-nextjs";
import ArrowWithLongArm from "components/Shared/icons/ArrowWithLongArm";
import IsRtl from "components/Shared/isRtl";
import { ComponentProps } from "lib/component-props";
import Image from "next/image";
import { useState } from "react";
import CheckmarkCircleIcon from "components/Shared/icons/checkmark-circle-icon";
import { DGAButton } from "components/Shared/dga-button/DGAButton";

type SocialChannelsProps = ComponentProps & {
  fields: any;
};

const SocialChannels = (props: SocialChannelsProps): JSX.Element => {
  const [copiedFieldId, setCopiedFieldId] = useState<string | null>(null);

  const handleCopy = async (text: string, fieldId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedFieldId(fieldId);
      setTimeout(() => setCopiedFieldId(null), 1500);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const getFieldHref = (field: any): string | null => {
    const fieldType = field?.fields?.Type?.fields?.Title?.value?.toLowerCase();
    const fieldValue = field?.fields?.Field?.value;

    if (!fieldValue) return null;

    if (fieldType === "phone") {
      return `tel:${fieldValue}`;
    } else if (fieldType === "email") {
      return `mailto:${fieldValue}`;
    }

    return null;
  };

  return (
    <section aria-labelledby={props?.fields?.Title?.value ? "social-channels-title" : undefined}>
      <header className="bg-primary-25 py-10 font-semibold text-3brand">
        <h1 id="social-channels-title" className=" responsive ">{props?.fields?.Title?.value}</h1>
      </header>
      <div className="responsive py-10">
        <div className="grid lg:grid-cols-4 gap-5 sm:grid-cols-2 grid-cols-1 ">
          {props?.fields?.Items?.length > 0 &&
            props?.fields?.Items?.map((item: any, index: number) => {
              return (
                <div
                  key={index}
                  className="rounded-lg shadow-sm p-5 flex flex-col justify-between gap-3 animate-top-dga"
                  style={{
                    boxShadow:
                      "0px 2px 4px -2px rgba(16, 24, 40, 0.06),0px 4px 8px -2px rgba(16, 24, 40, 0.1)",
                  }}
                >
                  <div className="flex flex-col gap-2">
                    <span className="w-fit">
                      <Image
                        src={item?.fields?.Icon?.value?.src}
                        alt={item?.fields?.Icon?.value?.alt || item?.fields?.Title?.value || "Social media icon"}
                        width={48}
                        height={48}
                      />
                    </span>
                    <h2 className="font-bold text-primary-1000 text-[18px] mt-2 animate-top-dga">
                      {item?.fields?.Title?.value}
                    </h2>

                    {/* Contact Fields */}
                    {item?.fields?.Fields?.length > 0 && (
                      <div className="flex flex-col gap-3">
                        {item.fields.Fields.map((field: any, fieldIndex: number) => {
                          const label = field?.fields?.Label?.value;
                          const fieldValue = field?.fields?.Field?.value;
                          const fieldType = field?.fields?.Type?.fields?.Title?.value?.toLowerCase();
                          const fieldId = field?.id || `${index}-${fieldIndex}`;
                          const href = getFieldHref(field);
                          const isPhone = fieldType === "phone";
                          const isEmail = fieldType === "email";
                          // Only show if Label exists


                          return (
                            <div key={fieldId} className="flex items-center gap-1 relative">
                              {label && <span className="text-neutral-700 text-sm font-normal">
                                {label}:
                              </span>}
                              {fieldValue && (
                                <div className="flex items-center gap-2">
                                  {href ? (
                                    <a
                                      href={href}
                                      className="text-[#1B8354] text-sm font-normal hover:underline"
                                    >
                                      {fieldValue}
                                    </a>
                                  ) : (
                                    <span className="text-neutral-800 text-sm">
                                      {fieldValue}
                                    </span>
                                  )}
                                  {(isPhone || isEmail) && (
                                    <button
                                      onClick={() => handleCopy(fieldValue, fieldId)}
                                      className="relative flex items-center justify-center w-4 h-4 text-[#1B8354] hover:opacity-70 transition-opacity"
                                      aria-label={`Copy ${fieldValue}`}
                                    >
                                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden={true} focusable="false">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M7.95727 1.04199H7.87575C6.54053 1.04198 5.48273 1.04197 4.64588 1.14089C3.78674 1.24245 3.07949 1.45564 2.4887 1.94049C2.28827 2.10498 2.10449 2.28876 1.94 2.48919C1.45516 3.07997 1.24196 3.78722 1.1404 4.64637C1.04149 5.48322 1.04149 6.54101 1.0415 7.87623V7.95775C1.04149 9.29297 1.04149 10.3508 1.1404 11.1876C1.24196 12.0468 1.45516 12.754 1.94 13.3448C2.10449 13.5452 2.28827 13.729 2.4887 13.8935C3.04859 14.353 3.71308 14.5685 4.5127 14.6763C5.16176 14.7637 5.94304 14.7849 6.88376 14.7902C6.89462 15.3086 6.91866 15.7639 6.97194 16.1601C7.07279 16.9102 7.28853 17.5418 7.79013 18.0434C8.29173 18.545 8.92329 18.7607 9.67338 18.8616C10.3959 18.9587 11.3145 18.9587 12.4541 18.9587H13.3789C14.5186 18.9587 15.4372 18.9587 16.1596 18.8616C16.9097 18.7607 17.5413 18.545 18.0429 18.0434C18.5445 17.5418 18.7602 16.9102 18.8611 16.1601C18.9582 15.4376 18.9582 14.519 18.9582 13.3794V12.4546C18.9582 11.3149 18.9582 10.3963 18.8611 9.67387C18.7602 8.92378 18.5445 8.29222 18.0429 7.79062C17.5413 7.28902 16.9097 7.07328 16.1596 6.97243C15.7634 6.91915 15.3081 6.8951 14.7897 6.88425C14.7844 5.94353 14.7632 5.16224 14.6758 4.51319C14.568 3.71357 14.3525 3.04908 13.893 2.48919C13.7285 2.28876 13.5447 2.10498 13.3443 1.94049C12.7535 1.45565 12.0463 1.24245 11.1871 1.14089C10.3503 1.04197 9.29249 1.04198 7.95727 1.04199ZM13.5396 6.87533C13.534 5.95018 13.5132 5.24603 13.437 4.68012C13.3463 4.00698 13.1845 3.59627 12.9267 3.28218C12.8142 3.14504 12.6885 3.0193 12.5513 2.90676C12.2202 2.63497 11.7815 2.46986 11.0404 2.38225C10.2855 2.29302 9.3016 2.29199 7.91651 2.29199C6.53141 2.29199 5.54754 2.29302 4.79261 2.38225C4.05148 2.46985 3.61286 2.63497 3.28169 2.90676C3.14456 3.0193 3.01881 3.14504 2.90627 3.28218C2.63448 3.61335 2.46937 4.05197 2.38176 4.7931C2.29253 5.54803 2.2915 6.5319 2.2915 7.91699C2.2915 9.30209 2.29253 10.286 2.38176 11.0409C2.46937 11.782 2.63448 12.2206 2.90627 12.5518C3.01881 12.6889 3.14456 12.8147 3.28169 12.9272C3.59578 13.185 4.00649 13.3468 4.67964 13.4375C5.24554 13.5137 5.94969 13.5345 6.87485 13.5401C6.87484 13.487 6.87484 13.4334 6.87484 13.3794V12.4546C6.87482 11.3149 6.87481 10.3963 6.97194 9.67387C7.07279 8.92378 7.28853 8.29221 7.79013 7.79062C8.29173 7.28902 8.92329 7.07328 9.67338 6.97243C10.3959 6.8753 11.3145 6.87531 12.4541 6.87533H13.3789C13.433 6.87533 13.4865 6.87532 13.5396 6.87533ZM8.67401 8.6745C8.90465 8.44387 9.22846 8.29349 9.83994 8.21128C10.4694 8.12665 11.3037 8.12533 12.4998 8.12533H13.3332C14.5294 8.12533 15.3636 8.12665 15.9931 8.21128C16.6046 8.29349 16.9284 8.44387 17.159 8.6745C17.3896 8.90514 17.54 9.22895 17.6222 9.84043C17.7068 10.4699 17.7082 11.3041 17.7082 12.5003V13.3337C17.7082 14.5298 17.7068 15.3641 17.6222 15.9936C17.54 16.605 17.3896 16.9288 17.159 17.1595C16.9284 17.3901 16.6045 17.5405 15.9931 17.6227C15.3636 17.7073 14.5294 17.7087 13.3332 17.7087H12.4998C11.3037 17.7087 10.4694 17.7073 9.83994 17.6227C9.22846 17.5405 8.90465 17.3901 8.67401 17.1595C8.44338 16.9288 8.293 16.605 8.21079 15.9936C8.12616 15.3641 8.12484 14.5298 8.12484 13.3337V12.5003C8.12484 11.3041 8.12617 10.4699 8.21079 9.84043C8.29301 9.22895 8.44338 8.90514 8.67401 8.6745Z" fill="#1B8354" />
                                      </svg>

                                      {copiedFieldId === fieldId && (
                                        <span
                                          className={`
                                        absolute bottom-full ${IsRtl() ? "-right-2" : "-left-2"
                                            } mb-1 px-2 py-1 text-primary-1000 text-xs
                                        bg-neutral-100 border border-neutral-300 rounded transition-all duration-300
                                        ${copiedFieldId === fieldId ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}
                                        pointer-events-none flex gap-1 items-center whitespace-nowrap
                                      `}
                                        >
                                          <CheckmarkCircleIcon height="12" width="12" />
                                          {/* {IsRtl() ? "تم النسخ" : "Copied"} */}
                                        </span>
                                      )}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {item?.fields?.BtnUrl?.value?.href && (
                    <div className="self-end mt-2">
                      <DGAButton
                        buttonContent={
                          <span className="inline-flex items-center gap-2 max-w-[200px]">
                            <ArrowWithLongArm
                              className={`flex-shrink-0 fill-primary-1000 ${IsRtl() ? "transition rotate-[180deg]" : ""}`}
                              aria-hidden={true}
                            />
                            <span className="truncate text-sm font-medium text-primary-1000">
                              {item?.fields?.BtnText?.value || "View details"}
                            </span>
                          </span>
                        }
                        size="lg"
                        variant="secondarySolid"
                        className="!bg-neutral-100 hover:!bg-neutral-200 max-w-full"
                        onClick={() => {
                          if (item?.fields?.BtnUrl?.value?.target === "_blank") {
                            window.open(item?.fields?.BtnUrl?.value?.href, "_blank");
                          } else {
                            window.location.href = item?.fields?.BtnUrl?.value?.href;
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </section>
  );
};

export default withDatasourceCheck()<SocialChannelsProps>(SocialChannels);
